import { experimentBucket } from "./experimentBucket";

export const SIGNUP_PLAN_EXPERIMENT = "signup_plan_interstitial_v1";
export const SIGNUP_PLAN_PATH = "/signup/plans";
export type SignupPlanVariant = "control" | "treatment";
export const signupPlanVariant = (userId: string): SignupPlanVariant =>
  experimentBucket(SIGNUP_PLAN_EXPERIMENT, userId) < 0.1
    ? "treatment"
    : "control";

type Signup = {
  userId: string;
  provider: string;
  variant: SignupPlanVariant;
  forced: boolean;
  createdAt: number;
};
const KEY = SIGNUP_PLAN_EXPERIMENT;
const MAX_AGE = 2 * 60 * 60 * 1000;
const FORCE_KEY = "signupPlanForce";

// QA: open any page with ?signupPlan=force before signing up; ?signupPlan=off clears it.
export const captureSignupPlanForce = (search: string) => {
  const value = new URLSearchParams(search).get("signupPlan");
  if (value !== "force" && value !== "off") return;
  try {
    if (value === "force") localStorage.setItem(FORCE_KEY, "1");
    if (value === "off") localStorage.removeItem(FORCE_KEY);
  } catch {
    /* Storage blocked: the tester gets their real bucket. */
  }
  // Keep OAuth assertion bytes unchanged while removing the QA parameter.
  const remaining = search
    .replace(/^\?/, "")
    .split("&")
    .filter((part) => !new URLSearchParams(part).has("signupPlan"))
    .join("&");
  return remaining ? `?${remaining}` : "";
};

const takeForce = () => {
  try {
    const forced = localStorage.getItem(FORCE_KEY) === "1";
    localStorage.removeItem(FORCE_KEY);
    return forced;
  } catch {
    return false;
  }
};

export const rememberSignup = (userId: string, provider: string): Signup => {
  const forced = takeForce();
  const signup: Signup = {
    userId,
    provider,
    variant: forced ? "treatment" : signupPlanVariant(userId),
    forced,
    createdAt: Date.now(),
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(signup));
  } catch {
    /* Storage restrictions must not block signup. */
  }
  return signup;
};

export const clearSignupPlan = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* Best effort. */
  }
};

// Consumed at the final onboarding handoff, never by an auth or profile guard.
export const takeSignupPlan = (userId?: string): Signup | null => {
  let signup: Signup | null = null;
  try {
    signup = JSON.parse(sessionStorage.getItem(KEY) || "null");
  } catch {
    /* Fail open. */
  }
  clearSignupPlan();
  if (
    !signup ||
    signup.userId !== userId ||
    Date.now() - signup.createdAt > MAX_AGE ||
    signup.variant !== "treatment"
  )
    return null;
  return signup;
};
